import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { catalogApi, type Catalog } from '../../api/catalog';

const NAV_LINKS = [
  { to: '/catalog/dashboard', label: 'Dashboard' },
  { to: '/catalog/categories/tree', label: 'Category tree' },
  { to: '/catalog/categories/new', label: 'New category' },
  { to: '/catalog/classification', label: 'Classification' },
  { to: '/catalog/classification/review', label: 'AI review' },
  { to: '/catalog/marketplace-mapping', label: 'Marketplace mapping' },
  { to: '/catalog/collections', label: 'Collections' },
  { to: '/catalog/collections/smart', label: 'Smart collections' },
  { to: '/catalog/seo', label: 'SEO categories' },
  { to: '/catalog/taxonomy', label: 'Taxonomy explorer' },
  { to: '/catalog/hsn', label: 'HSN mapping' },
  { to: '/catalog/gst', label: 'GST classification' },
];

export function CatalogListPage() {
  const [items, setItems] = useState<Catalog[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const reload = () => {
    setLoading(true);
    void catalogApi
      .listCatalogs()
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load catalogs'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, []);

  const create = async () => {
    if (!name.trim() || !code.trim()) return;
    try {
      await catalogApi.createCatalog({ name: name.trim(), code: code.trim() });
      setName('');
      setCode('');
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create catalog');
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Catalog & Taxonomy</h1>
          <p className="mt-1 text-sm text-slate-600">Enterprise product catalogs, categories, and classification.</p>
        </div>
      </div>
      <nav className="mt-4 flex flex-wrap gap-3 text-sm">
        {NAV_LINKS.map((link) => (
          <Link key={link.to} to={link.to} className="text-slate-700 hover:underline">
            {link.label}
          </Link>
        ))}
      </nav>
      <div className="mt-6 flex flex-wrap gap-2 rounded-xl border bg-white p-4">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Catalog code"
          className="rounded-lg border px-3 py-2 text-sm"
          aria-label="Catalog code"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Catalog name"
          className="rounded-lg border px-3 py-2 text-sm"
          aria-label="Catalog name"
        />
        <button type="button" onClick={() => void create()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
          Create catalog
        </button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {loading ? (
        <p className="mt-6">Loading…</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-xl border bg-white">
          <table className="min-w-full text-sm">
            <thead className="border-b bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Version</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3">{c.code}</td>
                  <td className="px-4 py-3">{c.status}</td>
                  <td className="px-4 py-3">{c.version}</td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    No catalogs yet. Create one to start building your taxonomy.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
